// Club-timezone-aware date helpers.
// Reads timezone from settings/branding (set on the AdminBranding page);
// falls back to Eastern until loaded.
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

let clubTz = 'America/New_York';

// Self-initialize (fire and forget; helpers use fallback until resolved)
getDoc(doc(db, 'settings', 'branding'))
  .then(snap => { if (snap.exists() && snap.data().timezone) clubTz = snap.data().timezone; })
  .catch(() => {});

export const setClubTimezone = (tz) => { if (tz) clubTz = tz; };

// YYYY-MM-DD for any Date, in the club's timezone (en-CA gives ISO ordering)
export const dateStr = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: clubTz }).format(d);

// Today's date string in the club's timezone
export const localToday = () => dateStr(new Date());

/**
 * Hours from now until a club-local wall time, e.g. hoursUntil('2026-09-01', '18:00').
 *
 * Mirrors hoursUntilBooking in functions/index.js. Both exist because the
 * reservation guardrails compare lead time on each side, and a browser in a
 * different timezone than the club must not compute a different answer than the
 * server — the dialog would refuse a booking the server would take, or worse.
 * Never `new Date('YYYY-MM-DDTHH:MM')`: that reads the wall time in the
 * BROWSER's zone, which is the bug this helper exists to avoid.
 *
 * Returns NaN for a malformed date, which callers treat as "don't know".
 */
export const hoursUntil = (dateString, timeString) => {
  const [y, m, d] = String(dateString).split('-').map(Number);
  const [hh, mm] = String(timeString || '00:00').split(':').map(Number);
  if (!y || !m || !d) return NaN;
  // Ask what offset the club zone was on at roughly that moment, then shift.
  const guess = Date.UTC(y, m - 1, d, hh || 0, mm || 0);
  const tzName = new Intl.DateTimeFormat('en-US', {
    timeZone: clubTz, timeZoneName: 'longOffset',
  }).formatToParts(new Date(guess)).find(p => p.type === 'timeZoneName')?.value || 'GMT+00:00';
  const mo = /GMT([+-])(\d{2}):(\d{2})/.exec(tzName);
  const offsetMin = mo ? (mo[1] === '-' ? -1 : 1) * (Number(mo[2]) * 60 + Number(mo[3])) : 0;
  return (guess - offsetMin * 60000 - Date.now()) / 3600000;
};

// ── Birthdays ────────────────────────────────────────────────────────────
//
// Registration only ever checked that a birthday was PRESENT, so a date
// picker left on the current year sailed through: two adults were stored
// with 2026 birthdays, which read as age 0 anywhere age is derived.
//
// Bounds are deliberately loose — the job is catching a slipped year, not
// policing who may join. Anything a real member could plausibly enter passes.

const MAX_AGE = 110;
const MIN_AGE_ADULT = 13;
const MIN_AGE_JUNIOR = 4;

/** Whole years between a YYYY-MM-DD string and today, in the club timezone. */
export const ageFrom = (birthday) => {
  if (!birthday) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(birthday).trim());
  if (!m) return null;
  const [, y, mo, d] = m.map(Number);
  const born = new Date(y, mo - 1, d);
  // Compare date-parts only: constructing today from the club-timezone string
  // keeps this from flipping a birthday by a day near midnight.
  const [ty, tm, td] = localToday().split('-').map(Number);
  if (born.getFullYear() !== y || born.getMonth() !== mo - 1 || born.getDate() !== d) return null;
  let age = ty - y;
  if (tm < mo || (tm === mo && td < d)) age--;
  return age;
};

/**
 * Why this birthday can't be right, or '' if it's fine.
 *
 * Empty input passes — whether a birthday is REQUIRED is each form's call,
 * and every caller already asks that question separately.
 */
export const birthdayError = (birthday, { athleteType = '' } = {}) => {
  if (!birthday) return '';
  const age = ageFrom(birthday);
  if (age === null) return 'Enter a valid date';
  if (age < 0) return 'Birthday cannot be in the future';
  if (age > MAX_AGE) return 'Check the year on this birthday';
  const junior = String(athleteType).toLowerCase() === 'junior';
  const min = junior ? MIN_AGE_JUNIOR : MIN_AGE_ADULT;
  if (age < min) {
    return junior
      ? 'Check the year — that would make them under 4'
      : `Check the year — that would make them ${age}`;
  }
  return '';
};

// ── When a class starts, as an instant ────────────────────────────────────
//
// Stored on every attendance row so a security rule can compare it against
// request.time. The rule is what actually stops a parent taking back a
// check-in after the session has run; the buttons only stop them trying.
//
// A rule cannot do this itself: the row carries `date` as "YYYY-MM-DD" and the
// start time lives on the class doc, and rules have no way to parse a string
// into something comparable.
//
// Built from the browser's clock, which matches how every existing check-in
// screen already decides whether a class has started. For staff at the club
// that is the club's timezone. Someone checking athletes in from another
// timezone would shift the deadline by the offset — worth knowing, and the
// same as today's behaviour rather than a regression.
export const classStartsAt = (cls) => {
  const date = cls?.date;
  const time = cls?.startTime;
  if (!date || !time) return null;
  const d = new Date(`${date}T${time}`);
  return Number.isNaN(d.getTime()) ? null : d;
};
